import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import api, { formatApiError, money } from "@/lib/api";
import { PageHeader, EmptyState, StatCard } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Receipt, ArrowClockwise, MagnifyingGlass, TrendUp, TrendDown, Wallet, DownloadSimple,
} from "@phosphor-icons/react";
import DateRangePicker, { rangeToParams } from "@/components/DateRangePicker";
import { downloadCsv } from "@/lib/csv";

const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

const SOURCE_LABEL = {
  request: "Payment request",
  manual: "Admin adjustment",
  white_label: "White labeling",
  call_limit: "Call-limit purchase",
  ai_model: "AI Model",
};

const sourceBadgeClass = (source) => {
  if (source === "request") return "bg-primary/10 text-primary border-primary/20";
  if (source === "white_label") return "bg-violet-500/10 text-violet-700 border-violet-500/20";
  if (source === "call_limit") return "bg-teal-500/10 text-teal-700 border-teal-500/20";
  if (source === "ai_model") return "bg-amber-500/10 text-amber-800 border-amber-500/20";
  return "bg-muted text-muted-foreground border-border";
};

const last30 = () => {
  const to = new Date();
  to.setHours(0, 0, 0, 0);
  const from = new Date(to);
  from.setDate(from.getDate() - 29);
  return { from, to };
};

/**
 * Admin payment history.
 * channel="direct"   → platform/direct customers only (default)
 * channel="reseller" → reseller accounts only (not their customers)
 */
export default function PaymentHistory({ channel = "direct" }) {
  const isResellerChannel = channel === "reseller";
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ added_minor: 0, deducted_minor: 0, net_minor: 0 });
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("all");
  const [range, setRange] = useState(last30);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { channel, ...rangeToParams(range) };
      if (source !== "all") params.source = source;
      const { data } = await api.get("/billing/payment-history", { params });
      if (Array.isArray(data)) {
        setRows(data);
        let added = 0, deducted = 0;
        data.forEach((r) => {
          const a = r.amount_minor || 0;
          if (a >= 0) added += a; else deducted += -a;
        });
        setSummary({ added_minor: added, deducted_minor: deducted, net_minor: added - deducted });
      } else {
        setRows(data?.rows || []);
        setSummary(data?.summary || { added_minor: 0, deducted_minor: 0, net_minor: 0 });
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [channel, source, range]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setSource("all");
    setQ("");
    setRange(last30());
  }, [channel]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => {
      const hay = [
        r.account_name, r.account_company, r.account_email, r.reseller_name,
        r.description, r.request_note, r.processed_by_name, r.account_type,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [rows, q]);

  const cards = useMemo(() => {
    if (!q.trim()) return summary;
    let added = 0, deducted = 0;
    filtered.forEach((r) => {
      const a = r.amount_minor || 0;
      if (a >= 0) added += a; else deducted += -a;
    });
    return { added_minor: added, deducted_minor: deducted, net_minor: added - deducted };
  }, [filtered, summary, q]);

  const exportCsv = () => {
    if (!filtered.length) {
      toast.error("Nothing to export");
      return;
    }
    downloadCsv(
      isResellerChannel ? "reseller-payments.csv" : "customer-payments.csv",
      [
        { label: "Date", value: (r) => fmtDate(r.approved_at || r.created_at) },
        { label: "Account", value: (r) => r.account_name || "" },
        { label: "Email", value: (r) => r.account_email || "" },
        { label: "Type", value: (r) => r.account_type || "" },
        { label: "Source", value: (r) => SOURCE_LABEL[r.source] || r.source || "" },
        { label: "Description", value: (r) => r.description || r.request_note || "" },
        { label: "Processed by", value: (r) => r.processed_by_name || "" },
        { label: "Amount (cents)", value: (r) => r.amount_minor ?? "" },
        { label: "Balance after (cents)", value: (r) => r.balance_after_minor ?? "" },
      ],
      filtered,
    );
  };

  return (
    <div data-testid={isResellerChannel ? "reseller-payment-history-page" : "payment-history-page"}>
      <PageHeader
        title={isResellerChannel ? "Reseller Payment" : "Customer Payments"}
        subtitle={isResellerChannel
          ? "Payment activity for reseller accounts only — requests, adjustments, white labeling, call-limit, and AI Model."
          : "Payment activity for direct customers only — payment requests, adjustments, call-limit, and AI Model."}
      >
        <div className="flex items-center gap-2">
          <Button variant="outline" className="rounded-lg" onClick={exportCsv} disabled={!filtered.length}
            data-testid="payment-history-export">
            <DownloadSimple className="w-4 h-4 mr-1.5" weight="bold" /> Export CSV
          </Button>
          <Button variant="outline" className="rounded-lg" onClick={load} data-testid="payment-history-refresh">
            <ArrowClockwise className="w-4 h-4 mr-1.5" /> Refresh
          </Button>
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard
          testid="payment-history-added"
          icon={TrendUp}
          tone="success"
          label="Added"
          value={loading ? "…" : money(cards.added_minor || 0)}
          hint="Credits in selected period"
        />
        <StatCard
          testid="payment-history-deducted"
          icon={TrendDown}
          tone="warning"
          label="Deducted"
          value={loading ? "…" : money(cards.deducted_minor || 0)}
          hint="Debits in selected period"
        />
        <StatCard
          testid="payment-history-net"
          icon={Wallet}
          tone="primary"
          label="Total (net)"
          value={loading ? "…" : money(cards.net_minor || 0)}
          hint="Added − deducted"
        />
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="space-y-1.5 min-w-[220px] flex-1 sm:flex-none sm:w-[260px]">
          <p className="text-xs text-muted-foreground">Date range</p>
          <DateRangePicker value={range} onChange={(r) => setRange(r?.from ? r : last30())} testid="payment-history-dates" />
        </div>
        <div className="space-y-1.5 min-w-[160px]">
          <p className="text-xs text-muted-foreground">Source</p>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger data-testid="payment-history-source" className="rounded-lg"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="request">Payment request</SelectItem>
              <SelectItem value="manual">Admin adjustment</SelectItem>
              {isResellerChannel && <SelectItem value="white_label">White labeling</SelectItem>}
              <SelectItem value="call_limit">Call-limit purchase</SelectItem>
              <SelectItem value="ai_model">AI Model</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 flex-1 min-w-[200px]">
          <p className="text-xs text-muted-foreground">Search</p>
          <div className="relative">
            <MagnifyingGlass className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="payment-history-search"
              className="pl-9 rounded-lg"
              placeholder={isResellerChannel ? "Reseller, email, note…" : "Company, email, note…"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          testid="payment-history-empty"
          icon={Receipt}
          title={isResellerChannel ? "No reseller payments" : "No customer payments"}
          description={isResellerChannel
            ? "No reseller account entries match these filters."
            : "No direct-customer entries match these filters."}
        />
      ) : (
        <div className="rounded-xl border border-border bg-card card-shadow overflow-hidden" data-testid="payment-history-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Account</TableHead>
                {!isResellerChannel && <TableHead>Type</TableHead>}
                <TableHead>Source</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Processed by</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Balance after</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => {
                const credit = r.amount_minor >= 0;
                return (
                  <TableRow key={r.id} data-testid={`payment-history-row-${r.id}`}>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {fmtDate(r.approved_at || r.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{r.account_name || "—"}</div>
                      {r.account_email && (
                        <div className="text-xs text-muted-foreground">{r.account_email}</div>
                      )}
                    </TableCell>
                    {!isResellerChannel && (
                      <TableCell className="capitalize text-sm">{r.account_type || "—"}</TableCell>
                    )}
                    <TableCell className="text-sm">
                      <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs ${sourceBadgeClass(r.source)}`}>
                        {SOURCE_LABEL[r.source] || r.source}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[240px] truncate"
                      title={[r.description, r.request_note].filter(Boolean).join(" — ")}>
                      {r.description || r.request_note || "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.processed_by_name || "—"}</TableCell>
                    <TableCell className={`text-right tabular-nums font-semibold ${credit ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
                      {credit ? "+" : "−"}{money(Math.abs(r.amount_minor || 0))}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{money(r.balance_after_minor || 0)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
